import { createContext, useCallback, useContext, useState } from "react";

/**
 * The shared floating-panel state for Mira / Chat / Calendar.
 *
 * MON-2: the state lives in a top-level PROVIDER (mounted in App, ABOVE the routes) rather than
 * inside LayoutShell. LayoutShell is a *descendant* of each page, so a page that both rendered
 * LayoutShell AND read `openPanel` in its own body (the Home Mira rail) was reading a context its
 * own return value provided — i.e. the default `{openPanel:null}` — and never saw the launcher open.
 * Hoisting the provider above the pages makes the page body and the launchers share ONE context.
 *
 * TEACH-UX P1-a — the provider now OWNS open/close rather than observing it.
 *
 * Before this it was an advisory signal: each launcher kept its own `useState` and merely REPORTED
 * into the context so the other two could hide their buttons. "One panel at a time" was therefore a
 * convention that three components had to keep cooperating to honour, and the button-hiding existed
 * only because three FABs could otherwise overlap an open panel. With one FAB left, the hiding is
 * moot — but the mutual exclusion is not, because all three panels still occupy the same corner.
 * So the invariant moves into the one place that can actually enforce it: opening any panel closes
 * whatever was open, structurally, because `openPanel` holds a single name.
 *
 * THE STANDALONE FALLBACK is why this is safe to adopt. `usePanelOpen` prefers provider state and
 * falls back to component-local state when no provider is mounted, so a launcher rendered bare in a
 * test behaves EXACTLY as it did before — its own open state drives it, nothing hides it. Without
 * that, moving state into the provider would have broken every standalone launcher test, and the
 * temptation would have been to wrap those tests in a provider — which would have quietly changed
 * what they were testing.
 */
export const FloatingPanelContext = createContext({
  openPanel: null,
  setOpenPanel: () => {},
  // Only the real provider sets this. The default context is inert, and that is load-bearing.
  __isProvider: false,
});

export const useFloatingPanel = () => useContext(FloatingPanelContext);

/**
 * `[open, setOpen]` for one named panel — the same shape a `useState` returned, so call sites read
 * unchanged. Under a provider, opening this panel closes any other; standalone, it is local state.
 *
 * @param name the panel's name ("mira" | "chat" | "calendar").
 */
export function usePanelOpen(name) {
  const ctx = useContext(FloatingPanelContext);
  const [localOpen, setLocalOpen] = useState(false);
  const hasProvider = ctx.__isProvider === true;
  const open = hasProvider ? ctx.openPanel === name : localOpen;

  const setOpen = useCallback(
    (next) => {
      const resolve = (prev) => (typeof next === "function" ? next(prev) : next);
      if (!hasProvider) {
        setLocalOpen((prev) => Boolean(resolve(prev)));
        return;
      }
      ctx.setOpenPanel((prev) => {
        const wasOpen = prev === name;
        const shouldOpen = Boolean(resolve(wasOpen));
        // Opening claims the single slot; closing releases it only if we still hold it, so a stale
        // close from a panel that already lost the slot cannot shut the one that took it.
        if (shouldOpen) return name;
        return wasOpen ? null : prev;
      });
    },
    [ctx, hasProvider, name],
  );

  return [open, setOpen];
}

export function FloatingPanelProvider({ children }) {
  const [openPanel, setOpenPanel] = useState(null);
  return (
    <FloatingPanelContext.Provider value={{ openPanel, setOpenPanel, __isProvider: true }}>
      {children}
    </FloatingPanelContext.Provider>
  );
}
